/**
 * Plays a brand's HLS in a <video>: hls.js wherever the browser has Media Source Extensions
 * (loaded only when a player opens), native HLS only where it does not (iPhone Safari). Some
 * desktop browsers answer "maybe" for HLS and then cannot play it, so native is the fallback.
 */

import { useEffect, useRef, useState } from "react";

export function useHls(src: string | null) {
  const ref = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const video = ref.current;
    if (!video || !src) return;
    setError("");
    let destroy = () => {};
    let cancelled = false;
    void import("hls.js").then(({ default: Hls }) => {
      if (cancelled) return;
      if (Hls.isSupported()) {
        const hls = new Hls({ maxBufferLength: 20 });
        hls.on(Hls.Events.ERROR, (_e, data) => { if (data.fatal) setError("The video did not load. Check your connection and try again."); });
        hls.loadSource(src);
        hls.attachMedia(video);
        destroy = () => hls.destroy();
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
        video.onerror = () => setError("The video did not load.");
        destroy = () => { video.removeAttribute("src"); video.load(); };
      } else {
        setError("This browser cannot play the video here.");
      }
    });
    return () => { cancelled = true; destroy(); };
  }, [src]);
  return { ref, error };
}
